import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './History.css';

function History() {
  const { hasPermission } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const isAdmin = hasPermission('employees', 'read');

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/history/employees');
      setEmployees(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (employeeId) => {
    try {
      setLoading(true);
      const response = await api.get(`/history/employee/${employeeId}`);
      setEmployeeDetails(response.data);
      setSelectedEmployee(employeeId);
      setShowDetailsModal(true);
      setActiveTab('overview');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setEmployeeDetails(null);
    setSelectedEmployee(null);
    setActiveTab('overview');
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="container">
        <div className="card">
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading && !employeeDetails) {
    return <div className="loading">Loading employee history...</div>;
  }

  return (
    <div className="container">
      <h1>📊 Employee History</h1>
      
      <div className="card">
        <h2>All Employees</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
              <th>Goals</th>
              <th>Attendance</th>
              <th>Leaves</th>
              <th>Assets</th>
              <th>Requests</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee._id}>
                <td>{employee.firstName} {employee.lastName}</td>
                <td>{employee.email}</td>
                <td>{employee.role?.name || 'N/A'}</td>
                <td>{employee.department || 'N/A'}</td>
                <td>{employee.stats.totalGoals}</td>
                <td>{employee.stats.totalAttendance}</td>
                <td>{employee.stats.totalLeaves}</td>
                <td>{employee.stats.totalAssets}</td>
                <td>{employee.stats.totalRequests}</td>
                <td>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => fetchEmployeeDetails(employee._id)}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {employees.length === 0 && (
          <p style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
            No employees found
          </p>
        )}
      </div>

      {showDetailsModal && employeeDetails && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {employeeDetails.employee.firstName} {employeeDetails.employee.lastName}
              </h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="employee-info-card">
              <div className="info-row">
                <span className="label">Email:</span>
                <span className="value">{employeeDetails.employee.email}</span>
              </div>
              <div className="info-row">
                <span className="label">Role:</span>
                <span className="value">{employeeDetails.employee.role?.name || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="label">Department:</span>
                <span className="value">{employeeDetails.employee.department || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="label">Phone:</span>
                <span className="value">{employeeDetails.employee.phone || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="label">Joined:</span>
                <span className="value">{formatDate(employeeDetails.employee.createdAt)}</span>
              </div>
            </div>

            <div className="tabs">
              <button
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                📊 Overview
              </button>
              <button
                className={`tab ${activeTab === 'goals' ? 'active' : ''}`}
                onClick={() => setActiveTab('goals')}
              >
                🎯 Goals ({employeeDetails.goals.data.length})
              </button>
              <button
                className={`tab ${activeTab === 'attendance' ? 'active' : ''}`}
                onClick={() => setActiveTab('attendance')}
              >
                📅 Attendance ({employeeDetails.attendance.data.length})
              </button>
              <button
                className={`tab ${activeTab === 'leaves' ? 'active' : ''}`}
                onClick={() => setActiveTab('leaves')}
              >
                ✈️ Leaves ({employeeDetails.leaves.data.length})
              </button>
              <button
                className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
                onClick={() => setActiveTab('assets')}
              >
                💼 Assets ({employeeDetails.assets.data.length})
              </button>
              <button
                className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                📝 Requests ({employeeDetails.requests.data.length})
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-grid">
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <h3>🎯 Goals</h3>
                    <p className="big-number">{employeeDetails.goals.stats.totalGoalsCreated}</p>
                    <small>{employeeDetails.goals.stats.totalGoalsCompleted} completed ({employeeDetails.goals.stats.completionRate}%)</small>
                  </div>
                  
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                    <h3>📅 Attendance</h3>
                    <p className="big-number">{employeeDetails.attendance.stats.presentDays}</p>
                    <small>Present out of {employeeDetails.attendance.stats.totalDays} days</small>
                  </div>
                  
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                    <h3>✈️ Leaves</h3>
                    <p className="big-number">{employeeDetails.leaves.stats.total}</p>
                    <small>{employeeDetails.leaves.stats.approved} approved, {employeeDetails.leaves.stats.pending} pending</small>
                  </div>
                  
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                    <h3>💼 Assets</h3>
                    <p className="big-number">{employeeDetails.assets.stats.total}</p>
                    <small>Total assigned assets</small>
                  </div>
                  
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                    <h3>📝 Requests</h3>
                    <p className="big-number">{employeeDetails.requests.stats.total}</p>
                    <small>{employeeDetails.requests.stats.resolved} resolved, {employeeDetails.requests.stats.pending} pending</small>
                  </div>
                </div>
              )}

              {activeTab === 'goals' && (
                <div className="details-section">
                  {employeeDetails.goals.data.length > 0 ? (
                    employeeDetails.goals.data.map((goal) => (
                      <div key={goal._id} className="detail-card">
                        <div className="detail-header">
                          <h4>{goal.project?.name || 'No Project'}</h4>
                          <span className="detail-date">{formatDate(goal.createdAt)}</span>
                        </div>
                        <div className="goals-list">
                          {goal.goals.map((g, index) => (
                            <div key={index} className="goal-item">
                              <input 
                                type="checkbox" 
                                checked={g.completed} 
                                readOnly 
                                disabled
                              />
                              <span className={g.completed ? 'completed' : ''}>{g.text}</span>
                            </div>
                          ))}
                        </div>
                        <div className="detail-footer">
                          <span>{goal.goals.filter(g => g.completed).length} / {goal.goals.length} completed</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No goals found</p>
                  )}
                </div>
              )}

              {activeTab === 'attendance' && (
                <div className="details-section">
                  {employeeDetails.attendance.data.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Login Time</th>
                          <th>Logout Time</th>
                          <th>Goals Created</th>
                          <th>Goals Completed</th>
                          <th>Working Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeDetails.attendance.data.map((att) => (
                          <tr key={att._id}>
                            <td>{formatDate(att.date)}</td>
                            <td>
                              <span className={`badge badge-${
                                att.status === 'present' ? 'success' : 
                                att.status === 'absent' ? 'danger' : 
                                'warning'
                              }`}>
                                {att.status}
                              </span>
                            </td>
                            <td>{att.loginTime ? formatTime(att.loginTime) : 'N/A'}</td>
                            <td>{att.logoutTime ? formatTime(att.logoutTime) : 'N/A'}</td>
                            <td>{att.goalsCreated}</td>
                            <td>{att.goalsCompleted}</td>
                            <td>{att.workingHours ? `${att.workingHours.toFixed(2)} hrs` : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="empty-state">No attendance records found</p>
                  )}
                </div>
              )}

              {activeTab === 'leaves' && (
                <div className="details-section">
                  {employeeDetails.leaves.data.length > 0 ? (
                    employeeDetails.leaves.data.map((leave) => (
                      <div key={leave._id} className="detail-card">
                        <div className="detail-header">
                          <h4>{leave.leaveType}</h4>
                          <span className={`badge badge-${
                            leave.status === 'approved' ? 'success' : 
                            leave.status === 'rejected' ? 'danger' : 
                            'warning'
                          }`}>
                            {leave.status}
                          </span>
                        </div>
                        <p><strong>Dates:</strong> {formatDate(leave.startDate)} - {formatDate(leave.endDate)}</p>
                        <p><strong>Reason:</strong> {leave.reason}</p>
                        {leave.adminResponse && (
                          <p><strong>Admin Response:</strong> {leave.adminResponse}</p>
                        )}
                        <div className="detail-footer">
                          <span>Applied: {formatDate(leave.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No leave records found</p>
                  )}
                </div>
              )}

              {activeTab === 'assets' && (
                <div className="details-section">
                  {employeeDetails.assets.data.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Asset Name</th>
                          <th>Type</th>
                          <th>Serial Number</th>
                          <th>Condition</th>
                          <th>Assigned Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeDetails.assets.data.map((asset) => (
                          <tr key={asset._id}>
                            <td>{asset.name}</td>
                            <td>{asset.type}</td>
                            <td>{asset.serialNumber}</td>
                            <td>
                              <span className={`badge badge-${
                                asset.condition === 'Excellent' || asset.condition === 'Good' ? 'success' : 
                                asset.condition === 'Fair' ? 'warning' : 
                                'danger'
                              }`}>
                                {asset.condition}
                              </span>
                            </td>
                            <td>{formatDate(asset.assignedDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="empty-state">No assets assigned</p>
                  )}
                </div>
              )}

              {activeTab === 'requests' && (
                <div className="details-section">
                  {employeeDetails.requests.data.length > 0 ? (
                    employeeDetails.requests.data.map((request) => (
                      <div key={request._id} className="detail-card">
                        <div className="detail-header">
                          <h4>{request.subject}</h4>
                          <span className={`badge badge-${
                            request.status === 'resolved' ? 'success' : 
                            request.status === 'rejected' ? 'danger' : 
                            request.status === 'in-progress' ? 'info' :
                            'warning'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                        <p><strong>Category:</strong> {request.category}</p>
                        <p><strong>Description:</strong> {request.description}</p>
                        {request.adminResponse && (
                          <div className="admin-response">
                            <strong>Admin Response:</strong>
                            <p>{request.adminResponse}</p>
                          </div>
                        )}
                        <div className="detail-footer">
                          <span>Submitted: {formatDate(request.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No requests found</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default History;
